<script setup lang="ts">
/* The standard content container inside a page. */
withDefaults(
  defineProps<{
    title?: string
    /** Small line under the title. */
    description?: string
    /** Remove the inner padding — for tables that go edge to edge. */
    flush?: boolean
  }>(),
  { flush: false },
)
</script>

<template>
  <section class="panel">
    <header v-if="title || $slots.actions" class="header">
      <div class="headings">
        <h2 v-if="title" class="title">{{ title }}</h2>
        <p v-if="description" class="description">{{ description }}</p>
      </div>
      <div v-if="$slots.actions" class="actions"><slot name="actions" /></div>
    </header>
    <div :class="flush ? 'bodyFlush' : 'body'"><slot /></div>
  </section>
</template>

<style scoped>
.panel {
  display: flex;
  flex-direction: column;
  background-color: var(--color-surface);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-md);
  border-bottom: var(--border-width) solid var(--color-border);
}

.headings {
  min-width: 0;
}

.title {
  font-size: var(--text-base);
  font-weight: var(--weight-semibold);
}

.description {
  margin-top: var(--space-3xs);
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2xs);
  flex: none;
  max-width: 100%;
}

.body {
  padding: var(--space-md);
}

.bodyFlush {
  padding: 0;
}

/* Once the actions have wrapped under the title on a phone, let them use the
   whole row -- a filter box can then stretch instead of sitting at 40vw. */
@media (max-width: 40rem) {
  .actions {
    flex: 1 1 100%;
  }
}
</style>
